import { useMemo } from 'react'
import { clusterOverlappingDurations } from '../utils/calendar'
import dayjs, { Dayjs } from 'dayjs'
import { Slot, useCalendarSlots } from './use-calendar-slots'
import { MIN_COLUMN_WIDTH, RIGHT_PADDING, ROW_OFFSET, TIME_COLUMN_WIDTH, TIME_INTERVAL } from '../constants'
import { Appointment, UserAppointment } from '$api-client/types.gen'

export type UseAppointmentPositionProps = {
  data: UserAppointment[],
  gridWidth: number,
  slotHeight: number,
  getSlots: ReturnType<typeof useCalendarSlots>['getSlots'],
  weekDays: Dayjs[],
}

export const calculatePositions = (
  data: Appointment[],
  columnOffset: number,
  getSlots: (weekDay: number) => Slot[],
  gridWidth: number,
  numOfDays: number,
  groupSize: number,
  weekDays: Dayjs[],
  slotHeight: number,
) => {
  if (gridWidth === 0) {
    return []
  }

  const clusters = clusterOverlappingDurations(data)

  return data.map((appointment, index) => {
    const start = dayjs.utc(appointment.start_at)
    const end = dayjs.utc(appointment.finish_at)

    const gridDayIndex = weekDays.findIndex(day => day.isSame(start, 'day'))

    if (gridDayIndex === -1) {
      return null
    }

    const slots = getSlots(gridDayIndex)

    const cellWidth = Math.max(MIN_COLUMN_WIDTH, ((gridWidth - TIME_COLUMN_WIDTH) / numOfDays) / groupSize)
    const pxPerMinute = slotHeight / TIME_INTERVAL

    const slotStart = slots.findIndex(slot => start.isBefore(slot.finish) && start.isAfter(slot.start.subtract(1, 'millisecond')))
    const slotEnd = slots.findIndex(slot => end.isBefore(slot.finish) && end.isAfter(slot.start.subtract(1, 'millisecond')))

    // Handle cases where appointment is outside the available slots
    if (slotStart === -1 || slotEnd === -1) {
      return null // Skip this appointment if it doesn't fit within available slots
    }

    const slotStartItem = slots.at(slotStart)!
    const slotEndItem = slots.at(slotEnd)!

    // For gridRowEnd, we need the next slot after the one containing the end time
    const gridRowEnd = slotEnd + 1

    // For height calculation, we use the actual slot data
    const totalRows = slotEnd - slotStart + 1

    // Calculate size & position
    const startDifference = slotStartItem.start.diff(start, 'minutes')
    const startAdjustedDifference = startDifference < 0 ? Math.abs(startDifference) : 0
    const finishDifference = slotEndItem.finish.diff(end, 'minutes')

    const topOffset = (startAdjustedDifference * pxPerMinute)
    const bottomOffset = finishDifference * pxPerMinute

    const height = (totalRows * slotHeight) - topOffset - bottomOffset

    const width = (cellWidth - RIGHT_PADDING) / clusters[index].length
    const leftOffset = width * (clusters[index][0] % clusters[index].length)

    return {
      position: {
        column: gridDayIndex * groupSize + columnOffset,
        slotStart: slotStart + ROW_OFFSET,
        slotEnd: gridRowEnd,
      },
      offset: {
        left: leftOffset,
        top: topOffset,
      },
      width,
      height,
      data: appointment,
    }
  })
}

export function useAppointmentPosition({ data, gridWidth, slotHeight, getSlots, weekDays }: UseAppointmentPositionProps) {

  const positions = useMemo(() => data.map((item, index) => calculatePositions(
    item.appointments,
    index,
    getSlots,
    gridWidth,
    weekDays.length,
    data.length,
    weekDays,
    slotHeight,
  ).filter(position => position !== null)), [data, gridWidth, slotHeight, weekDays, getSlots])

  return positions
}
